import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  Alert,
  AlertEntity,
  AlertPriority,
  AlertType,
  AnalyticsInsightAlertEntity,
  EventAlertEntity,
  PriorityConfig,
  SafeguardingInsightsAlertEntity,
} from 'src/app/models/alert';
import { findPriorityConfig } from 'src/app/utilities/alert.utils';
import { AlertViewModel } from './alert.view-model';
import { EventNoShowAlertViewModel } from './event-no-show-alert.view-model';
import { NudityAlertViewModel } from './nudity-alert.view-model';
import { SafeguardingAlertViewModel } from './safeguarding-alert.view-model';
import { TeacherDeclinedAlertViewModel } from './teacher-declined-alert.view-model';

@Injectable({ providedIn: 'root' })
export class AlertViewModelFactory {
  constructor(private readonly translateService: TranslateService) {}

  /**
   * A getter, not a constructor field: this service can be injected before the
   * locale JSON has loaded, and `instant()` returns the raw key until it has.
   * Each read builds a new array, so snapshot it before binding in a template
   * (see `AlertPriorityDropdownComponent`).
   */
  get priorityConfigs(): PriorityConfig[] {
    return [
      {
        id: AlertPriority.CRITICAL,
        label: this.translateService.instant('Critical'),
        icon: 'warning_amber_critical',
      },
      {
        id: AlertPriority.HIGH,
        label: this.translateService.instant('High'),
        icon: 'warning_amber_high',
      },
      {
        id: AlertPriority.MEDIUM,
        label: this.translateService.instant('Medium'),
        icon: 'warning_amber',
      },
      {
        id: AlertPriority.LOW,
        label: this.translateService.instant('Low'),
        icon: 'warning_amber_minor',
      },
    ];
  }

  get(alert: Alert<AlertEntity>): AlertViewModel<AlertEntity> | null {
    // Casts are safe because createAlert always pairs alertType with the matching
    // entityClass (POST_SESSION_SAFEGUARDING → SpaceRecordingSafeguardingInsights,
    // SESSION_NUDITY → AnalyticsInsight, event types → Event). GET /alerts returns
    // that pairing; switch is the runtime check. A full alertType discriminant
    // would remove the casts but is more machinery than this factory boundary needs.
    const priorityConfigs = this.priorityConfigs;
    const priorityConfig = findPriorityConfig(priorityConfigs, alert.priority);
    switch (alert.alertType) {
      case AlertType.TEACHER_DECLINED_EVENT:
        return new TeacherDeclinedAlertViewModel(
          alert as Alert<EventAlertEntity>,
          this.translateService,
          priorityConfig,
        );
      case AlertType.EVENT_NO_SHOW:
        return new EventNoShowAlertViewModel(
          alert as Alert<EventAlertEntity>,
          this.translateService,
          priorityConfig,
        );
      case AlertType.POST_SESSION_SAFEGUARDING:
        return new SafeguardingAlertViewModel(
          alert as Alert<SafeguardingInsightsAlertEntity>,
          this.translateService,
          priorityConfigs,
          priorityConfig,
        );
      case AlertType.SESSION_NUDITY:
        return new NudityAlertViewModel(
          alert as Alert<AnalyticsInsightAlertEntity>,
          this.translateService,
          priorityConfig,
        );
      default:
        return null;
    }
  }
}
